import React, {
  useCallback,
  useRef,
  useState,
  createContext,
  useContext,
  useEffect,
} from "react";
import { createPortal } from "react-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  CheckCircle2,
  AlertCircle,
  AlertTriangle,
  Info,
  X,
} from "lucide-react";

export type NotificationType = "success" | "error" | "warning" | "info";

export interface NotificationAction {
  label: string;
  onClick: () => void;
}

export interface NotificationProps {
  type: NotificationType;
  message: string;
  title?: string;
  duration?: number;
  action?: NotificationAction;
}

export interface ActiveNotification extends NotificationProps {
  id: string;
  remaining: number;
  totalDuration: number;
  isPaused: boolean;
}

const NotificationsStateContext = createContext<{
  notifications: ActiveNotification[];
  addNotification: (notification: NotificationProps) => void;
  removeNotification: (id: string) => void;
  pauseNotification: (id: string) => void;
  resumeNotification: (id: string) => void;
  clearNotifications: () => void;
} | null>(null);

export const NotificationsProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [notifications, setNotifications] = useState<ActiveNotification[]>([]);
  const notificationsRef = useRef<ActiveNotification[]>([]);
  const lastFrameTime = useRef<number>(Date.now());
  const animationFrameId = useRef<number | null>(null);

  const tick = useCallback(() => {
      const now = Date.now();
      const delta = now - lastFrameTime.current;
      lastFrameTime.current = now;

      let needsCleanup = false;

      notificationsRef.current.forEach((n) => {
          if (!n.isPaused) {
              n.remaining -= delta;
          }
          if (n.remaining <= 0) {
              needsCleanup = true;
          }
      });

      if (needsCleanup) {
          const active = notificationsRef.current.filter((n) => n.remaining > 0);
          notificationsRef.current = active;
          setNotifications([...active]);
      }

      if (notificationsRef.current.length > 0) {
          animationFrameId.current = requestAnimationFrame(tick);
      } else {
          animationFrameId.current = null;
      }
  }, []);

  const startLoop = useCallback(() => {
      if (!animationFrameId.current) {
          lastFrameTime.current = Date.now();
          animationFrameId.current = requestAnimationFrame(tick);
      }
  }, [tick]);

  const addNotification = useCallback((req: NotificationProps) => {
      const duration = req.duration || (req.type === "error" ? 8000 : 4000);
      const id = Math.random().toString(36).substring(2, 9);

      const newNotification: ActiveNotification = {
          ...req,
          id,
          remaining: duration,
          totalDuration: duration,
          isPaused: false,
      };

      setNotifications((prev) => {
          const filtered = prev.filter(
              (n) => !(n.message === req.message && n.type === req.type)
          );

          const nextState = [...filtered, newNotification];
          notificationsRef.current = nextState;
          return nextState;
      });

      startLoop();
  }, [startLoop]);

  const removeNotification = useCallback((id: string) => {
      setNotifications((prev) => {
          const nextState = prev.filter((n) => n.id !== id);
          notificationsRef.current = nextState;
          return nextState;
      });
  }, []);

  const pauseNotification = useCallback((id: string) => {
      const note = notificationsRef.current.find((n) => n.id === id);
      if (note) note.isPaused = true;
  }, []);

  const resumeNotification = useCallback((id: string) => {
      const note = notificationsRef.current.find((n) => n.id === id);
      if (note) {
          note.isPaused = false;
          lastFrameTime.current = Date.now();
          startLoop();
      }
  }, [startLoop]);

  const clearNotifications = useCallback(() => {
      notificationsRef.current = [];
      setNotifications([]);
  }, []);

  return (
      <NotificationsStateContext.Provider
          value={{
              notifications,
              addNotification,
              removeNotification,
              pauseNotification,
              resumeNotification,
              clearNotifications,
          }}
      >
          {children}
      </NotificationsStateContext.Provider>
  );
};

// --- Notification Item Component ---

const getNotificationStyles = (type: NotificationType) => {
  switch (type) {
      case "success":
          return {
              icon: CheckCircle2,
              iconColor: "text-emerald-500",
              borderColor: "border-l-emerald-500",
              progressColor: "bg-emerald-500",
              glow: "shadow-emerald-500/10",
          };
      case "error":
          return {
              icon: AlertCircle,
              iconColor: "text-red-500",
              borderColor: "border-l-red-500",
              progressColor: "bg-red-500",
              glow: "shadow-red-500/10",
          };
      case "warning":
          return {
              icon: AlertTriangle,
              iconColor: "text-amber-500",
              borderColor: "border-l-amber-500",
              progressColor: "bg-amber-500",
              glow: "shadow-amber-500/10",
          };
      case "info":
      default:
          return {
              icon: Info,
              iconColor: "text-blue-500",
              borderColor: "border-l-blue-500",
              progressColor: "bg-blue-500",
              glow: "shadow-blue-500/10",
          };
  }
};

const NotificationItem: React.FC<{
  notification: ActiveNotification;
  onClose: (id: string) => void;
  onPause: (id: string) => void;
  onResume: (id: string) => void;
}> = ({ notification, onClose, onPause, onResume }) => {
  const { id, type, title, message, totalDuration, action } = notification;
  const styles = getNotificationStyles(type);
  const Icon = styles.icon;

  const progressBarRef = useRef<HTMLDivElement>(null);
  const animationRef = useRef<number | null>(null);

  useEffect(() => {
      const updateProgress = () => {
          if (progressBarRef.current) {
              const currentProgress = Math.max(0, notification.remaining / totalDuration);
              progressBarRef.current.style.transform = `scaleX(${currentProgress})`;
          }
          animationRef.current = requestAnimationFrame(updateProgress);
      };

      animationRef.current = requestAnimationFrame(updateProgress);

      return () => {
          if (animationRef.current) cancelAnimationFrame(animationRef.current);
      };
  }, [notification, totalDuration]);

  return (
      <motion.div
          layout
          initial={{ opacity: 0, x: 50, scale: 0.95 }}
          animate={{ opacity: 1, x: 0, scale: 1 }}
          exit={{ opacity: 0, scale: 0.9, transition: { duration: 0.2 } }}
          whileHover={{ scale: 1.02 }}
          onMouseEnter={() => onPause(id)}
          onMouseLeave={() => onResume(id)}
          className={`
              relative w-full max-w-sm mb-3 overflow-hidden rounded-xl 
              bg-white/95 backdrop-blur-md border border-gray-100 border-l-4 
              ${styles.borderColor} shadow-xl ${styles.glow} pointer-events-auto
          `}
          style={{ willChange: "transform" }}
      >
          <div className="p-4 pr-10 flex items-start gap-3">
              <div className={`mt-0.5 flex-shrink-0 ${styles.iconColor}`}>
                  <Icon className="w-5 h-5" />
              </div>
              <div className="flex-1 min-w-0">
                  {title && (
                      <h4 className="font-bold text-sm text-gray-900 leading-tight mb-1">
                          {title}
                      </h4>
                  )}
                  <p className="text-sm text-gray-600 leading-snug break-words">
                      {message}
                  </p>
                  {action && (
                      <button
                          onClick={(e) => {
                              e.stopPropagation();
                              action.onClick();
                              onClose(id);
                          }}
                          className="mt-2 px-3 py-1.5 rounded-lg text-xs font-semibold bg-gray-100 hover:bg-gray-200 text-gray-800 transition-colors"
                      >
                          {action.label}
                      </button>
                  )}
              </div>
              <button
                  onClick={(e) => {
                      e.stopPropagation();
                      onClose(id);
                  }}
                  className="absolute top-2 right-2 p-1.5 text-gray-400 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
              >
                  <X className="w-4 h-4" />
              </button>
          </div>
          <div className="absolute bottom-0 left-0 right-0 h-[3px] bg-gray-100">
              <div
                  ref={progressBarRef}
                  className={`h-full ${styles.progressColor} origin-left`}
                  style={{ width: "100%", transform: "scaleX(1)", willChange: "transform" }}
              />
          </div>
      </motion.div>
  );
};

export const NotificationsPortal = () => {
  const context = useContext(NotificationsStateContext);

  if (!context || typeof document === "undefined") return null;

  const { notifications, removeNotification, pauseNotification, resumeNotification } = context;

  return createPortal(
      <div className="fixed top-0 right-0 z-[9999] p-4 sm:p-6 flex flex-col items-end pointer-events-none max-h-screen">
          <AnimatePresence mode="popLayout">
              {notifications.map((n) => (
                  <NotificationItem
                      key={n.id}
                      notification={n}
                      onClose={removeNotification}
                      onPause={pauseNotification}
                      onResume={resumeNotification}
                  />
              ))}
          </AnimatePresence>
      </div>,
      document.body
  );
};

const useNotify = () => {
  const context = useContext(NotificationsStateContext);

  if (!context) {
      throw new Error("useNotify must be used within a NotificationsProvider");
  }

  const { addNotification, removeNotification, clearNotifications } = context;

  const showToast = useCallback(
      (typeOrOptions: NotificationType | NotificationProps | any, maybeMessage?: string) => {
          if (typeof typeOrOptions === 'string') {
              const titleMap: Record<string, string> = {
                  success: 'Success',
                  error: 'Error',
                  warning: 'Warning',
                  info: 'Info'
              };
              addNotification({
                  type: typeOrOptions as NotificationType,
                  message: maybeMessage || '',
                  title: titleMap[typeOrOptions]
              });
          } else if (typeOrOptions && typeof typeOrOptions === 'object') {
              // Handle old format { type, message, title } if used
              const { type = 'info', message = '', title, duration, action } = typeOrOptions;
              let displayTitle = title;
              if (!displayTitle) {
                  if (type === 'success') displayTitle = 'Success';
                  if (type === 'error') displayTitle = 'Error';
                  if (type === 'warning') displayTitle = 'Warning';
                  if (type === 'info') displayTitle = 'Information';
              }
              addNotification({ type, message, title: displayTitle, duration, action });
          }
      },
      [addNotification]
  );

  return {
      showToast,
      success: (msg: string, title?: string) => showToast({ type: 'success', message: msg, title }),
      error: (msg: string, title?: string) => showToast({ type: 'error', message: msg, title }),
      warning: (msg: string, title?: string) => showToast({ type: 'warning', message: msg, title }),
      info: (msg: string, title?: string) => showToast({ type: 'info', message: msg, title }),
      dismiss: removeNotification,
      dismissAll: clearNotifications,
      hideToast: (id?: string) => id ? removeNotification(id) : clearNotifications(),
      hideAllToasts: clearNotifications,
  };
};

export default useNotify;
